import { equalP } from 'shared/lib/array';
import { isEqual, uniqBy } from 'lodash-es';
import { logCatch } from '@/lib/logger/logger';
import { ref } from 'vue';

export default function useGoalMutator(goalsSvc, debounce, dogma, store, changeParents) {
  const updateProperty = (value, property, goal) => {
    if (property.type === 'description' || property.edgeName === 'description') {
      if (isEqual(value, goal.description)) {
        return new Promise((resolve) => {
          resolve();
        });
      }
      return update({ ...goal, description: value }, { commitNowStore: false, commitNowDB: false });
    }
    if (property.type === 'goalCycle' || property.edgeName === 'goalCycle') {
      return update({ ...goal, goalCycle: value }, { commitNowStore: true });
    }
    if (property.type === 'parents') {
      return changeParents({ goals: [goal], parents: value.map((uid) => ({ uid })) });
    }
    return update(
      {
        ...goal,
        properties: uniqBy([value, ...goal.properties], (item) => item.property.uid),
      },
      { commitNowStore: true },
    ).then(() => new Promise((resolve) => {
      resolve();
    }));
  };

  const updateLoading = ref(false);
  const update = (
    goal,
    { commitNowStore = true, commitNowDB = false } = {
      commitNowStore: true,
      commitNowDB: false,
    },
    hookParameter = undefined,
  ) => {
    updateLoading.value = true;
    const recalculate = recalculateProgress(goal);
    commitToStore(goal, commitNowStore);
    return saveToDB(goal, commitNowDB, recalculate, hookParameter);
  };

  const recalculateProgress = (goal) => {
    const current = goalsSvc.selectSingle(goal.uid);
    if (current.progressMeasurement !== goal.progressMeasurement) {
      return true;
    }
    if (current.start !== goal.start) {
      return true;
    }
    if (current.end !== goal.end) {
      return true;
    }
    if (current.threshold !== goal.threshold) {
      return true;
    }
    if (current.thresholdArea !== goal.thresholdArea) {
      return true;
    }
    if (!equalP({ a: current.paysOnto, b: goal.paysOnto })) {
      return true;
    }
    return false;
  };

  const commitToStore = (goal, commitNow) => {
    const commit = () => {
      goalsSvc.updateSingle(goal, { commitToRemote: false });
    };

    if (commitNow) {
      return commit();
    }

    return debounce.debounce(commit, 1000);
  };

  const save = (entity, recalculateProgress, hookParameter) => goalsSvc.updateSingle(
    entity,
    { commitToLocal: false },
    hookParameter,
  )
    .then((data) => {
      if (recalculateProgress) {
        return goalsSvc.updateGoalParentsProgress([entity.uid]).then(() => goalsSvc.selectSingle(data.uid));
      }

      return goalsSvc.selectSingle(data.uid);
    }).catch(logCatch(() => {
      throw new Error('error updating goal');
    }))
    .finally(() => {
      updateLoading.value = false;
    });

  const saveToDB = (goal, commitNow, recalculateProgress, hookParameter) => {
    const update = (resolve, reject) => () => save(goal, recalculateProgress, hookParameter)
      .then((goal) => {
        resolve(goal);
      })
      .catch((err) => {
        reject(err);
      });
    if (commitNow) {
      return new Promise((resolve, reject) => {
        update(resolve, reject)();
      });
    }

    return new Promise((resolve, reject) => {
      debounce.debounce(update(resolve, reject), 1000);
    });
  };

  return { update, commitToStore, updateProperty };
}
